/**
 * O sistema selecionado: o da ficha aberta. É estado de interação — nasce
 * quando a ficha de um sistema abre (pelo mapa ou pela lista), troca quando
 * abre a de outro e acaba quando ela fecha; não vai para o link nem para as
 * preferências. Funções puras (testadas em `selecao.test.ts`).
 *
 * Guarda-se a CHAVE do sistema, nunca a feição: as feições da camada são
 * trocadas a cada quadro, e a chave é o que identifica o sistema de um quadro
 * para o outro (e o observado no previsto, que leva o mesmo `uid`).
 */
import type { Sistema } from './layer';
import { chaveDoSistema } from './registro';

type Identidade = Pick<Sistema, 'lim' | 'uid' | 'iuid'>;

export interface Selecao {
  /** `chaveDoSistema` do que foi clicado (sistema ou núcleo). */
  chave: string;
  /** O sistema a que ele pertence. */
  uid: number;
}

/**
 * A seleção que a ficha de `s` pede, ou null. Sem `uid` não há como saber quem
 * é o sistema no quadro seguinte (nem distingui-lo de outro sem `uid` no mesmo
 * quadro): não se seleciona nada.
 */
export function selecaoDe(s: Identidade): Selecao | null {
  return s.uid === null ? null : { chave: chaveDoSistema(s), uid: s.uid };
}

/**
 * É o da ficha aberta? Vale para o observado e para o previsto, que têm a
 * mesma identidade. Os demais — inclusive os núcleos do sistema selecionado, e
 * o sistema do núcleo selecionado — são desenhados como sempre: nada se atenua.
 */
export function ehOSelecionado(selecao: Selecao | null, s: Identidade): boolean {
  return !!selecao && s.uid !== null && chaveDoSistema(s) === selecao.chave;
}

/** A seleção depois de a ficha de `chave` fechar: só a dela é limpa (a de outra ficha, já aberta, fica). */
export function aoFecharFicha(atual: Selecao | null, chave: string | null): Selecao | null {
  return atual && atual.chave === chave ? null : atual;
}

/**
 * A chave do SISTEMA (primeiro limiar) a que o contorno `s` pertence — ele
 * mesmo, ou o sistema do núcleo. A trajetória e a previsão da ficha são as do
 * sistema: a trajetória (`k:"t"`) dos núcleos vem só por `uid`, sem como
 * separar um núcleo do outro. Sem a lista de limiares, só o próprio sistema
 * se resolve.
 */
export function chaveDoExterno(s: Identidade, limiares: readonly number[]): string | null {
  if (s.uid === null) return null;
  if (s.iuid === null) return chaveDoSistema(s);
  return limiares.length ? chaveDoSistema({ lim: limiares[0], uid: s.uid, iuid: null }) : null;
}

/** A chave do sistema da seleção (ver `chaveDoExterno`), ou null. */
export function sistemaDaSelecao(selecao: Selecao | null, limiares: readonly number[]): string | null {
  if (!selecao) return null;
  const [lim, , iuid] = selecao.chave.split('|');
  const numero = (v: string | undefined) => (v === undefined || v === '' ? null : Number(v));
  return chaveDoExterno({ lim: numero(lim), uid: selecao.uid, iuid: numero(iuid) }, limiares);
}
